/**
 * Client credentials → a bearer token, entirely in memory.
 *
 * The brief is explicit and this is the one place it bites: a client secret typed in
 * here must never touch localStorage, sessionStorage or any other disk-backed store —
 * only this component's own React state, which is gone the moment the tab closes or
 * this component unmounts. The minted TOKEN is allowed to outlive the keystrokes (it is
 * lifted to the console's state so other operations can reuse it for the hour it is
 * valid), but it too is memory-only — never written anywhere persistent.
 *
 * TWO SETS OF KEYS, EACH SAYING WHICH IT IS (Austin, 2026-09-24). The difference
 * between sandbox and production is the thing a developer must not get wrong, so
 * both are on this screen with their own heading, their own colour and their own
 * rule written next to them:
 *
 *   SANDBOX     id and secret are readable whenever you want them. Revealed with
 *               the eye, copied with the button, filled in for you on arrival.
 *   PRODUCTION  the secret is shown ONCE, at the moment it is created, and stored
 *               only as a hash thereafter. There is no eye on an existing one
 *               because there is nothing behind it — not here, not in support.
 */
import { FC, useEffect, useRef, useState } from 'react';

import { REACT_APP_API_HOST as API_HOST_BASE } from 'envs';
import { getOrigin } from 'utils';
import { useWebAuthn } from 'hooks';
import { WEBAUTH_URL } from 'components/login/store';

import { trimSlash } from './contract';

export interface TokenInfo {
	value: string;
	scope: string;
	expiresAt: number;
	/**
	 * WHICH ENVIRONMENT THIS TOKEN IS ACTUALLY IN.
	 *
	 * Read from `/v3/oauth/introspect`, not from whichever button was clicked:
	 * the credential decides, and a credential can disagree with the person
	 * using it. Believing you are in sandbox while running production keys is
	 * the one mistake on this page that costs real money and touches real
	 * people, so the indicator is derived from the token, never from intent.
	 */
	environment?: 'sandbox' | 'live' | null;
}

export type KeyMode = 'sandbox' | 'production';

/**
 * THE KEY THE CONSOLE IS ACTUALLY SENDING WITH.
 *
 * `KeyMode` is which environment you chose; this is which credential you
 * PICKED — the one "Use these keys" was pressed on. The console shows it on the
 * request itself, next to Send, because "which keys is this using" is a
 * question about a specific credential, not only about an environment: two
 * production keys with different scopes fail differently, and a badge reading
 * PRODUCTION tells you nothing about which of them is on the wire.
 */
export interface ActiveKey {
	/** `sandbox`, a production credential's id, or `typed` for a pasted one. */
	ref: string;
	environment: 'sandbox' | 'live';
	clientId: string;
	label: string;
}

/**
 * ONE LINE OF THE CREDENTIAL HISTORY, as `/console/credential_events` returns it.
 *
 * `credential_name`, `client_id` and `environment` are carried on the ENTRY
 * rather than looked up against the key list, because the most important
 * entries are about keys that no longer exist — a deletion cannot be rendered
 * by joining to the row it deleted.
 */
interface CredEvent {
	id: string;
	action: 'created' | 'rotated' | 'scopes_changed' | 'deleted' | 'token_minted';
	at: string;
	credential_id: string;
	client_id: string;
	credential_name: string | null;
	environment: 'sandbox' | 'live';
	actor: {
		kind: 'user' | 'api' | 'system';
		name?: string | null;
		email?: string | null;
		role?: string | null;
		clientId?: string | null;
	};
	detail?: Record<string, any>;
	ip?: string | null;
}

interface LiveKey {
	id: string;
	name: string | null;
	client_id: string;
	scopes?: string[];
	created_at?: string | null;
	last_used_at?: string | null;
}

const fmtRemaining = (ms: number) => {
	const total = Math.max(0, Math.round(ms / 1000));
	const m = Math.floor(total / 60);
	const s = total % 60;
	return `${m}:${String(s).padStart(2, '0')}`;
};

/**
 * THE STAMP IS ABSOLUTE, and "3 minutes ago" is the decoration.
 *
 * A history is read to answer "when exactly", often days later and often
 * alongside someone else's log — a relative age cannot be compared with
 * anything and stops being true the moment the page is left open. So the date
 * and the time to the second lead, and the relative age trails as a
 * convenience for the entries that are recent enough for it to mean something.
 */
const fmtStamp = (iso: string) => {
	const d = new Date(iso);
	if (Number.isNaN(d.getTime())) return iso;
	return d.toLocaleString(undefined, {
		year: 'numeric',
		month: 'short',
		day: '2-digit',
		hour: '2-digit',
		minute: '2-digit',
		second: '2-digit',
	});
};

const fmtAgo = (iso: string) => {
	const t = new Date(iso).getTime();
	if (Number.isNaN(t)) return null;
	const secs = Math.round((Date.now() - t) / 1000);
	if (secs < 0) return null;
	if (secs < 60) return 'just now';
	const mins = Math.round(secs / 60);
	if (mins < 60) return `${mins}m ago`;
	const hrs = Math.round(mins / 60);
	if (hrs < 24) return `${hrs}h ago`;
	const days = Math.round(hrs / 24);
	/* Past a fortnight the relative form stops helping — "63d ago" is work to
	   read and the absolute date is right beside it. */
	return days <= 14 ? `${days}d ago` : null;
};

/** A client id is public and identifying; shortened to fit a line, never masked. */
const shortId = (id: string) => (id && id.length > 14 ? `${id.slice(0, 8)}\u2026${id.slice(-4)}` : id);

/** Who did it, in words. A machine says so; an automatic action says so too. */
const actorLabel = (e: CredEvent): string => {
	const a = e.actor ?? {kind: 'system'};
	if (a.kind === 'api') return `API credential ${shortId(a.clientId ?? '')}`;
	if (a.kind === 'system') {
		const by = e.detail?.triggered_by;
		const who = by?.name || by?.email;
		return who ? `Automatic \u2014 while ${who} had the console open` : 'Automatic';
	}
	return a.name || a.email || 'A signed-in user';
};

/** What was done, in words, with the specifics the entry actually carries. */
const actionLabel = (e: CredEvent): string => {
	const env = e.environment === 'live' ? 'Production' : 'Sandbox';
	switch (e.action) {
		case 'created':
			return `${env} key created`;
		case 'rotated':
			return e.detail?.automatic
				? `Sandbox key rotated automatically (every ${e.detail?.ttl_hours ?? 24}h)`
				: `${env} key secret rotated`;
		case 'scopes_changed': {
			const added: string[] = e.detail?.added ?? [];
			const removed: string[] = e.detail?.removed ?? [];
			/* Named individually up to four, counted beyond that. A key going from
			   four scopes to twenty-four is one fact; spelling out all twenty
			   turns one entry into a paragraph and pushes everything around it
			   off the screen. The exact list is in the response that made the
			   change and in the key's own scope chips above. */
			const list = (xs: string[], sign: string) =>
				xs.length <= 4
					? xs.map(x => `${sign}${x}`).join(', ')
					: `${xs.slice(0, 3).map(x => `${sign}${x}`).join(', ')} and ${xs.length - 3} more`;
			const parts = [
				added.length ? list(added, '+') : null,
				removed.length ? list(removed, '\u2212') : null,
			].filter(Boolean);
			return `Scopes changed \u2014 ${parts.join('; ') || 'no change'}`;
		}
		case 'deleted':
			return `${env} key deleted`;
		case 'token_minted':
			return `${env} token minted`;
		default:
			return e.action;
	}
};

const ACTION_TONE: Record<string, string> = {
	created: 'ok',
	rotated: 'rev',
	scopes_changed: 'rev',
	deleted: 'rej',
	token_minted: 'ns',
};

const fmtDate = (v?: string | null) => {
	if (!v) return null;
	const d = new Date(v);
	return Number.isNaN(d.getTime()) ? null : d.toLocaleDateString();
};

const EyeIcon: FC<{ off?: boolean }> = ({ off }) => (
	<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
		<path d="M1.6 12S5.3 5.5 12 5.5 22.4 12 22.4 12 18.7 18.5 12 18.5 1.6 12 1.6 12Z" />
		<circle cx="12" cy="12" r="3.2" />
		{off ? <path d="M3 3l18 18" /> : null}
	</svg>
);

const CopyIcon: FC<{ done?: boolean }> = ({ done }) =>
	done ? (
		<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" aria-hidden="true">
			<path d="M4 12.5 9.5 18 20 6.5" />
		</svg>
	) : (
		<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
			<rect x="9" y="9" width="11" height="11" rx="2" />
			<path d="M15 5.5H6a1.5 1.5 0 0 0-1.5 1.5v9" />
		</svg>
	);

/**
 * Copy, with the one fallback that matters.
 *
 * `navigator.clipboard` needs a secure context, and this page is served over
 * plain http on localhost — which IS secure by origin, but a panel opened on a
 * LAN address is not, and there the promise rejects silently. The textarea
 * fallback is ugly and it works everywhere, which is the right trade for a
 * button whose entire job is that the value ends up on the clipboard.
 */
const copyText = async (value: string): Promise<boolean> => {
	try {
		await navigator.clipboard.writeText(value);
		return true;
	} catch {
		try {
			const el = document.createElement('textarea');
			el.value = value;
			el.setAttribute('readonly', '');
			el.style.position = 'fixed';
			el.style.opacity = '0';
			document.body.appendChild(el);
			el.select();
			const ok = document.execCommand('copy');
			document.body.removeChild(el);
			return ok;
		} catch {
			return false;
		}
	}
};

const CopyButton: FC<{ value: string; what: string }> = ({ value, what }) => {
	const [done, setDone] = useState(false);
	const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
	useEffect(() => () => (timer.current ? clearTimeout(timer.current) : undefined), []);
	return (
		<button
			type="button"
			className={`keybtn${done ? ' done' : ''}`}
			title={done ? 'Copied' : `Copy ${what}`}
			aria-label={done ? 'Copied' : `Copy ${what}`}
			disabled={!value}
			onClick={async () => {
				if (!(await copyText(value))) return;
				setDone(true);
				if (timer.current) clearTimeout(timer.current);
				timer.current = setTimeout(() => setDone(false), 1600);
			}}
		>
			<CopyIcon done={done} />
		</button>
	);
};

export const AuthPanel: FC<{
	baseUrl: string;
	token: TokenInfo | null;
	onToken: (t: TokenInfo | null) => void;
	/* The page shows the choice too, so it has to know about it. */
	onMode?: (m: KeyMode) => void;
	/* …and WHICH credential, so the request panel can name it beside Send. */
	onActiveKey?: (k: ActiveKey | null) => void;
	/* The drawer keeps this mounted while closed (it is hidden with CSS), so the
	   history poll below would otherwise run for the whole life of the page. */
	open?: boolean;
}> = ({ baseUrl, token, onToken, onMode, onActiveKey, open = true }) => {
	const [clientId, setClientId] = useState('');
	const [clientSecret, setClientSecret] = useState('');
	const [scope, setScope] = useState('');
	/**
	 * THE PRODUCTION FORM HAS ITS OWN FIELDS, and this is a correctness fix, not
	 * tidiness (Austin, 2026-09-24).
	 *
	 * Both cards used to write into the ONE pair of inputs above. The sandbox
	 * credential is prefilled into those on load, so opening the production card
	 * showed "Mint a production token" already filled in with a SANDBOX id and
	 * secret — and pressing Generate minted a sandbox token while the switch
	 * said Production. The only thing that caught it was the mismatch warning
	 * underneath, after the fact.
	 *
	 * Two environments, two sets of fields, and no code path that copies one
	 * into the other: a sandbox credential can no longer arrive in the
	 * production form at all.
	 */
	const [liveId, setLiveId] = useState('');
	const [liveSecret, setLiveSecret] = useState('');
	const [liveScope, setLiveScope] = useState('');
	const [showLiveSecret, setShowLiveSecret] = useState(false);
	const [busy, setBusy] = useState(false);
	const [err, setErr] = useState<string | null>(null);
	const [now, setNow] = useState(Date.now());
	const [showSecret, setShowSecret] = useState(false);
	/* The CHOICE is remembered; the secret never is. Which environment you meant
	   to be in is a preference, and losing it on every refresh is how someone
	   ends up on the wrong one without noticing. */
	const [mode, setMode] = useState<KeyMode>(() => {
		try {
			return localStorage.getItem('simplici-v3-key-mode') === 'production' ? 'production' : 'sandbox';
		} catch {
			return 'sandbox';
		}
	});
	useEffect(() => {
		if (!token) return undefined;
		const id = setInterval(() => setNow(Date.now()), 1000);
		return () => clearInterval(id);
	}, [token]);

	/* The choice is restored from storage, so the page must hear about it without
	   anyone clicking anything. */
	useEffect(() => {
		onMode?.(mode);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	/**
	 * WHICH CREDENTIAL IS ON THE WIRE.
	 *
	 * Set by "Use these keys", and by nothing else — a key is in use because
	 * somebody chose it, not because it happens to be on screen or happens to be
	 * the only one. It is dropped the moment the token is, so the page can never
	 * name a key it is no longer sending with.
	 */
	const [active, setActive] = useState<ActiveKey | null>(null);
	useEffect(() => {
		onActiveKey?.(active);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [active]);
	useEffect(() => {
		if (!token) setActive(null);
	}, [token]);

	/**
	 * YOUR OWN KEYS, ALREADY ON THE PAGE.
	 *
	 * A developer reading their own API docs is already signed in; making them
	 * go and find a client id before they can send a single request is a wall at
	 * the first step. `/console/credentials` answers to the PANEL session (not a
	 * v3 token — that would be the same wall) and returns this business's sandbox
	 * credential in full, plus its production credentials WITHOUT their secrets,
	 * because those are hashed and cannot be returned by anyone.
	 */
	const [sandbox, setSandbox] = useState<{
		id?: string;
		client_id: string;
		client_secret: string;
		scopes?: string[];
		expires_at?: string | null;
		ttl_hours?: number;
		rotated?: boolean;
	} | null>(null);
	const [allScopes, setAllScopes] = useState<string[]>([]);
	const [userEmail, setUserEmail] = useState('');
	const [editScopes, setEditScopes] = useState<string[] | null>(null);
	const [savingScopes, setSavingScopes] = useState(false);
	const [scopeNote, setScopeNote] = useState<string | null>(null);
	/* Per production key: which one is mid-action, and what it is saying. */
	const [liveBusy, setLiveBusy] = useState<string | null>(null);
	const [liveNote, setLiveNote] = useState<{ id: string; text: string; bad?: boolean } | null>(null);
	const [liveEdit, setLiveEdit] = useState<{ id: string; scopes: string[] } | null>(null);
	/* Outcomes that outlive the row they happened to. */
	const [cardNote, setCardNote] = useState<string | null>(null);
	const { authenticate, registerNewCredential } = useWebAuthn();
	/* What the passkey step is doing right now, so the OS dialog is never the
	   first thing a person sees with no explanation of what asked for it. */
	const [passkeyStep, setPasskeyStep] = useState<string | null>(null);
	const [live, setLive] = useState<LiveKey[]>([]);
	const [loaded, setLoaded] = useState(false);
	/* THE HISTORY. Reloaded after every mutation this panel performs, because a
	   log that does not show what you just did reads as a broken log rather than
	   as one that needs a refresh. */
	const [events, setEvents] = useState<CredEvent[] | null>(null);
	const [eventsErr, setEventsErr] = useState<string | null>(null);
	const [eventsBusy, setEventsBusy] = useState(false);
	const [allEvents, setAllEvents] = useState(false);
	/**
	 * WHY THE FIELDS ARE EMPTY, SAID OUT LOUD.
	 *
	 * This prefill used to fail in silence: an api.v3 still compiling, a panel
	 * session that has expired, and the drawer opens with two blank fields and a
	 * disabled button. There is nothing on screen to act on, and it reads as a
	 * broken button rather than a request that did not answer — which is exactly
	 * how it was reported. So the failure is shown, with the reason and a retry.
	 */
	const [loadErr, setLoadErr] = useState<string | null>(null);
	const [creating, setCreating] = useState(false);
	/* A live secret exists on this screen for exactly as long as this tab stays
	   open on it — it is never re-fetchable, so it is never stored either. */
	const [freshLive, setFreshLive] = useState<{ client_id: string; client_secret: string } | null>(null);
	const [showFresh, setShowFresh] = useState(false);

	const chooseMode = (m: KeyMode) => {
		setMode(m);
		onMode?.(m);
		try {
			localStorage.setItem('simplici-v3-key-mode', m);
		} catch {
			/* a browser that refuses storage still switches, it just forgets */
		}
		/* THE SWITCH CHANGES WHAT YOU ARE LOOKING AT, NOT WHAT YOU ARE USING.
		   It used to drop the token as well, on the reasoning that credentials do
		   not carry across environments — but they no longer can: each card has
		   its own fields, and a key is put to work by pressing "Use these keys"
		   on it. Dropping the token here meant glancing at the other tab
		   disconnected the console from the key you had chosen, which is not what
		   the control says it does. The in-use strip above reports what is
		   actually on the wire, whichever tab is open. */
		setShowSecret(false);
		setShowLiveSecret(false);
	};

	const session = (): string | null => {
		try {
			const raw = document.cookie
				.split('; ')
				.find(c => c.startsWith('user='))
				?.slice('user='.length);
			return raw ? JSON.parse(decodeURIComponent(raw))?.token ?? null : null;
		} catch {
			return null;
		}
	};

	const loadKeys = async (cancelled = () => false) => {
		setLoadErr(null);
		try {
			{
				const s = session();
				if (!s) {
					setLoadErr('Not signed in to the panel, so your own keys could not be loaded. Sign in, or paste a credential below.');
					return;
				}
				const res = await fetch(`${trimSlash(baseUrl)}/console/credentials`, {
					headers: { authorization: `Bearer ${s}` },
				});
				if (!res.ok) {
					setLoadErr(
						res.status === 401
							? 'Your panel session was not accepted, so your own keys could not be loaded. Sign in again, or paste a credential below.'
							: `Could not load your keys — ${trimSlash(baseUrl)}/console/credentials answered ${res.status}. Paste a credential below, or retry.`
					);
					return;
				}
				const body = await res.json();
				const d = body?.data ?? body;
				if (cancelled()) return;
				setAllScopes(Array.isArray(d?.available_scopes) ? d.available_scopes : []);
				setUserEmail(d?.user_email ?? '');
				if (d?.sandbox?.client_id) {
					setSandbox(d.sandbox);
					/* THE KEY ROTATED, SO WHAT IS IN THE FORM IS DEAD. Sandbox keys are
					   replaced every 24 hours (and any key predating that rule is replaced
					   on sight), so on a rotation the fields are OVERWRITTEN rather than
					   preserved — keeping what someone typed would keep a credential that
					   can no longer mint a token, and Send would fail for a reason nothing
					   on the screen explains. */
					if (d.sandbox.rotated) {
						setClientId(d.sandbox.client_id);
						setClientSecret(d.sandbox.client_secret || '');
						onToken(null);
					} else {
						setClientId(current => current || d.sandbox.client_id);
						setClientSecret(current => current || d.sandbox.client_secret || '');
					}
				}
				setLive(Array.isArray(d?.production) ? d.production : []);
				setLoaded(true);
			}
		} catch (e: any) {
			/* The console still works without this — a credential can be pasted —
			   so this is a message, not a broken page. */
			setLoadErr(
				`Could not reach ${trimSlash(baseUrl)}/console/credentials (${String(e?.message ?? e).slice(0, 80)}). ` +
					'Paste a credential below, or retry.'
			);
		}
	};

	const loadEvents = async () => {
		const s = session();
		if (!s) {
			setEventsErr('Sign in to the panel to see the history.');
			return;
		}
		setEventsBusy(true);
		setEventsErr(null);
		try {
			const res = await fetch(`${trimSlash(baseUrl)}/console/credential_events?limit=100`, {
				headers: {authorization: `Bearer ${s}`},
			});
			const body = await res.json().catch(() => null);
			if (!res.ok) {
				throw new Error(
					body?.data?.error?.message || body?.error?.message || `History unavailable (${res.status}).`
				);
			}
			/* NOT the `body?.data ?? body` unwrap the rest of this file uses. That
			   idiom assumes `data` is the envelope; here `data` IS the payload — an
			   array of entries — so unwrapping once lands on the array and looking
			   for `.data` on it yields nothing. The symptom is an empty history on
			   a page whose network tab plainly shows the rows, with no error to
			   explain it. Both shapes are accepted explicitly instead. */
			const rows = Array.isArray(body?.data)
				? body.data
				: Array.isArray(body?.data?.data)
					? body.data.data
					: [];
			setEvents(rows);
		} catch (e: any) {
			/* Shown, not swallowed: an empty history and an unreachable one look
			   identical, and only one of them means "nothing has happened". */
			setEventsErr(e?.message || 'Could not load the history.');
		} finally {
			setEventsBusy(false);
		}
	};

	useEffect(() => {
		let dead = false;
		loadKeys(() => dead);
		loadEvents();
		return () => {
			dead = true;
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [baseUrl]);

	/**
	 * THE HISTORY KEEPS ITSELF CURRENT (Austin, 2026-09-24).
	 *
	 * There is no Refresh button, because a log with one is a log you cannot
	 * trust at a glance: you never know whether what you are looking at is now
	 * or is whenever the drawer happened to open. Two things keep it honest —
	 * every mutation this panel performs reloads it immediately, and this poll
	 * catches everything else: a second person on another machine, a key
	 * created over the API, a sandbox rotation.
	 *
	 * IT ONLY RUNS WHEN SOMEONE IS LOOKING. The drawer stays mounted when
	 * closed, and a hidden tab still runs its timers, so both are checked —
	 * polling a log nobody has on screen is a request per user per interval for
	 * ever, bought with nothing.
	 */
	useEffect(() => {
		if (!open) return undefined;
		const tick = () => {
			if (!document.hidden) loadEvents();
		};
		const id = setInterval(tick, 8000);
		/* Straight away on opening, so the panel never shows a log from the last
		   time it was open. */
		tick();
		document.addEventListener('visibilitychange', tick);
		return () => {
			clearInterval(id);
			document.removeEventListener('visibilitychange', tick);
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [open, baseUrl]);

	const createLive = async () => {
		const s = session();
		if (!s) return;
		setCreating(true);
		setErr(null);
		try {
			const res = await fetch(`${trimSlash(baseUrl)}/console/credentials`, {
				method: 'POST',
				headers: { 'Content-Type': 'application/json', authorization: `Bearer ${s}` },
				body: JSON.stringify({ environment: 'live' }),
			});
			const body = await res.json().catch(() => null);
			const d = body?.data ?? body;
			if (!res.ok || !d?.client_secret) {
				throw new Error(d?.error?.message || body?.error?.message || `Could not create a production key — ${res.status}.`);
			}
			setFreshLive({ client_id: d.client_id, client_secret: d.client_secret });
			setShowFresh(true);
			loadEvents();
			setLive(current => [
				...current,
				{ id: d.id, name: d.name ?? null, client_id: d.client_id, created_at: d.created_at ?? null },
			]);
		} catch (e: any) {
			setErr(e?.message || 'Could not create a production key.');
		} finally {
			setCreating(false);
		}
	};

	/**
	 * A TOKEN FOR A KEY WHOSE SECRET CANNOT BE FETCHED.
	 *
	 * The ordinary mint needs an id and a secret, and a production secret is
	 * stored as a hash — so for a live key that button could never enable, which
	 * read as broken rather than as the rule it is. The panel session mints it
	 * instead: the person is signed in as the credential's owner, which is a
	 * better proof than holding a string.
	 */
	const mintFor = async (id: string, label: string, clientId?: string) => {
		if (liveBusy) return;
		const s = session();
		if (!s) return;
		/* Pressing "Use these keys" on a production key IS choosing production —
		   leaving the switch on sandbox while sending live requests is exactly the
		   disagreement the rest of this panel exists to surface. */
		if (mode !== 'production') {
			setMode('production');
			onMode?.('production');
			try {
				localStorage.setItem('simplici-v3-key-mode', 'production');
			} catch {
				/* a browser that refuses storage still switches, it just forgets */
			}
		}
		setLiveBusy(id);
		setLiveNote(null);
		try {
			const res = await fetch(`${trimSlash(baseUrl)}/console/credentials/${id}/token`, {
				method: 'POST',
				headers: { 'Content-Type': 'application/json', authorization: `Bearer ${s}` },
				body: JSON.stringify({}),
			});
			const body = await res.json().catch(() => null);
			const d = body?.data ?? body;
			if (!res.ok || !d?.access_token) {
				throw new Error(d?.error?.message || body?.error?.message || `Could not mint a token (${res.status}).`);
			}
			onToken({
				value: d.access_token,
				scope: d.scope ?? '',
				expiresAt: Date.now() + (d.expires_in ?? 3600) * 1000,
				environment: d.environment === 'live' ? 'live' : 'sandbox',
			});
			setActive({
				ref: id,
				environment: d.environment === 'live' ? 'live' : 'sandbox',
				clientId: clientId ?? d.client_id ?? '',
				label,
			});
			setLiveNote({ id, text: `In use. Every request on this page is now sent with ${label}.` });
			loadEvents();
		} catch (e: any) {
			/* A failed mint leaves whatever was already in use in use — the previous
			   token is still the one on the wire, so clearing the label here would
			   make the page disown a key it is still sending with. */
			setLiveNote({ id, text: e?.message || 'Could not mint a token.', bad: true });
		} finally {
			setLiveBusy(null);
		}
	};

	/**
	 * THE PASSKEY CHECK BEFORE A PRODUCTION KEY IS DESTROYED.
	 *
	 * Deleting a live key stops every integration using it, now, with no undo. A
	 * signed-in session does not answer "is the owner here" — an unlocked laptop
	 * answers yes — so this runs the same ceremony the platform uses at login
	 * (Touch ID / Face ID / security key, verified by the platform, not by us)
	 * and sends the session it returns as proof. v3 accepts it only while it is
	 * minutes old.
	 */
	const webauthn = async (body: Record<string, unknown>, s: string) => {
		const r = await fetch(`${API_HOST_BASE}${WEBAUTH_URL.CLIENT_WEBAUTHN}`, {
			method: 'POST',
			headers: { 'Content-Type': 'application/json', authorization: `Bearer ${s}` },
			/* `rpId` MUST BE THE FULL ORIGIN, not a hostname. clients.api derives the
			   relying-party id from it with extractHostNameFromURL, but it uses the
			   value AS SENT for `expectedOrigin` — so `localhost` makes the server
			   expect an origin of "localhost" while the browser's clientDataJSON says
			   "http://localhost:3020", and verification fails with a flat
			   `isRegistered: false` that names nothing. getOrigin() is what the login
			   flow sends, and this has to match it exactly. */
			body: JSON.stringify({ ...body, rpId: getOrigin() }),
		}).then(r2 => r2.json()).catch(() => null);
		return r?.data ?? r;
	};

	/**
	 * THE PASSKEY CHECK BEFORE A PRODUCTION KEY IS DESTROYED — and, if there is
	 * no passkey yet, the offer to set one up rather than a dead end.
	 *
	 * Deleting a live key stops every integration using it, now, with no undo. A
	 * signed-in session does not answer "is the owner here" — an unlocked laptop
	 * answers yes — so this runs the platform's own ceremony (the login one,
	 * verified by clients.api) and sends the session it returns as proof.
	 *
	 * AN ACCOUNT WITH NO PASSKEY IS NOT AN ERROR, IT IS A SETUP STEP. Telling
	 * someone "no passkey is registered, go to Login & security" is a dead end
	 * dressed as an explanation: they came here to delete a key, and the thing
	 * standing in the way is something we can offer them in place. Registration
	 * for a signed-in user returns a freshly minted session of its own, so
	 * setting one up IS the proof — there is no second ceremony afterwards.
	 */
	const biometricProof = async (): Promise<string> => {
		const s = session();
		if (!s) throw new Error('Sign in to the panel first.');

		const opts = await webauthn({ type: 'authenticateOpts', email: userEmail }, s);
		if (opts?.authenticateOptions) {
			setPasskeyStep('Confirm with your passkey — Touch ID, Face ID or your security key.');
			return new Promise<string>((resolve, reject) => {
				authenticate(
					async (payload: any) => {
						const v = await webauthn({ ...payload, type: 'verifyAuthentication' }, s);
						if (v?.isAuthenticated && v?.token) return resolve(v.token);
						reject(new Error(v?.message || 'The passkey was not accepted.'));
					},
					opts,
					(err: any) => reject(new Error(err?.message || 'The passkey check was cancelled.'))
				);
			});
		}

		/* No passkey on this account — set one up, here, now. */
		setPasskeyStep(
			'This account has no passkey yet. Your device will ask you to create one — ' +
				'that same check authorises this deletion.'
		);
		const reg = await webauthn({ type: 'registrationOptsUID' }, s);
		if (!reg?.registrationOptions) {
			throw new Error(
				reg?.message ??
					reg?.errorData?.message ??
					'This account cannot register a passkey right now, so a production key cannot be deleted here.'
			);
		}
		return new Promise<string>((resolve, reject) => {
			registerNewCredential(async (resp: any) => {
				const saved = await webauthn(
					{
						type: 'verifyRegistrationUID',
						registrationOptResponse: resp,
						id: reg.id,
						deviceInfo: navigator.userAgent ?? {},
					},
					s
				);
				if (saved?.isRegistered && saved?.token) {
					setPasskeyStep('Passkey registered — it will be used for this and future deletions.');
					return resolve(saved.token);
				}
				reject(new Error(saved?.message || 'The passkey could not be registered.'));
			}, reg.registrationOptions);
		});
	};

	const deleteLive = async (id: string, clientId: string) => {
		setLiveBusy(id);
		setPasskeyStep(null);
		setLiveNote({ id, text: 'Checking it is really you…' });
		try {
			const proof = await biometricProof();
			const s = session();
			const res = await fetch(`${trimSlash(baseUrl)}/console/credentials/${id}`, {
				method: 'DELETE',
				headers: {
					authorization: `Bearer ${s}`,
					'simplici-biometric-authorization': `Bearer ${proof}`,
				},
			});
			const body = await res.json().catch(() => null);
			const d = body?.data ?? body;
			if (!res.ok) throw new Error(d?.error?.message || body?.error?.message || `Delete refused (${res.status}).`);
			setLive(cur => cur.filter(k => k.id !== id));
			/* THE ROW IS GONE, SO ITS NOTE CANNOT LIVE ON IT. Keyed to the deleted
			   key, the confirmation unmounted with the row and the only feedback for
			   a destructive, irreversible action was the row silently disappearing. */
			setCardNote(`Deleted ${clientId.slice(0, 10)}… — ${d.note ?? ''}`);
			setLiveNote(null);
			loadEvents();
		} catch (e: any) {
			setLiveNote({ id, text: e?.message || 'Could not delete the key.', bad: true });
		} finally {
			setLiveBusy(null);
			setPasskeyStep(null);
		}
	};

	/**
	 * Mint a token from a client id and secret.
	 *
	 * `pick` is what "Use these keys" passes: the credential to mint from, and
	 * the name to put beside Send afterwards. Called with nothing, it mints from
	 * whatever is in the form — a pasted credential, which is named as such
	 * rather than attributed to a key on this screen.
	 */
	const mint = async (pick: {
		ref: string;
		clientId: string;
		clientSecret: string;
		scopeText: string;
		label: string;
	}) => {
		const id = pick.clientId;
		const secret = pick.clientSecret;
		const wantScope = pick.scopeText.trim();
		setBusy(true);
		setErr(null);
		try {
			const res = await fetch(`${trimSlash(baseUrl)}/oauth/token`, {
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify({
					grant_type: 'client_credentials',
					client_id: id,
					client_secret: secret,
					...(wantScope ? { scope: wantScope } : {}),
				}),
			});
			const data = await res.json().catch(() => null);
			if (!res.ok || !data?.access_token) {
				throw new Error(
					data?.error?.message ||
						`Token request failed — ${res.status} ${res.statusText}. Check the base URL and that the credential is real.`
				);
			}
			/* Ask the token what it is. A mismatch between this and the button that
			   was clicked is exactly what the indicator exists to catch. */
			let environment: 'sandbox' | 'live' | null = null;
			try {
				const who = await fetch(`${trimSlash(baseUrl)}/oauth/introspect`, {
					headers: { authorization: `Bearer ${data.access_token}` },
				});
				if (who.ok) {
					const body = await who.json();
					const env = (body?.data ?? body)?.environment;
					environment = env === 'live' || env === 'sandbox' ? env : null;
				}
			} catch {
				/* Unknown is shown as unknown; it is never assumed to be sandbox. */
			}
			onToken({
				value: data.access_token,
				scope: data.scope || wantScope,
				expiresAt: Date.now() + (data.expires_in ?? 3600) * 1000,
				environment,
			});
			/* The token's own word for its environment beats the button that was
			   pressed, here as everywhere else on this page. */
			setActive({
				ref: pick.ref,
				environment: environment ?? (mode === 'production' ? 'live' : 'sandbox'),
				clientId: id,
				label: pick.label,
			});
		} catch (e: any) {
			setErr(e?.message || 'Could not reach the token endpoint.');
			onToken(null);
			setActive(null);
		} finally {
			setBusy(false);
		}
	};

	/**
	 * "USE THESE KEYS" — the sandbox card.
	 *
	 * One press has to do the whole thing, because everything short of it is a
	 * step somebody can forget: switch the environment, put the credential in the
	 * form, mint the token, and say on the request which key it came from. It
	 * mints through `/oauth/token` with the real id and secret — the same call an
	 * integrator makes — rather than the panel-session shortcut, because for
	 * sandbox the secret is readable and the honest path is available.
	 */
	const useSandboxKeys = async () => {
		if (busy) return;
		/* The keys may not have arrived yet — fetch them rather than sitting there
		   unusable, which is what a disabled button would have done. */
		if (!sandbox?.client_id) {
			await loadKeys();
			return;
		}
		if (mode !== 'sandbox') chooseMode('sandbox');
		setClientId(sandbox.client_id);
		setClientSecret(sandbox.client_secret || '');
		setShowSecret(false);
		await mint({
			ref: 'sandbox',
			clientId: sandbox.client_id,
			clientSecret: sandbox.client_secret || '',
			scopeText: scope,
			label: 'Your sandbox key',
		});
	};

	/** Clears whichever form it was pressed on, and the token with it. */
	const forget = (which: 'sandbox' | 'production') => {
		if (which === 'production') {
			setLiveId('');
			setLiveSecret('');
			setLiveScope('');
			setShowLiveSecret(false);
		} else {
			setClientId('');
			setClientSecret('');
			setScope('');
			setShowSecret(false);
		}
		setErr(null);
		onToken(null);
	};

	const usingSandbox = !!sandbox && clientId === sandbox.client_id;
	const remainingMs = token ? token.expiresAt - now : 0;
	const expired = !!token && remainingMs <= 0;

	return (
		<div className={`auth-panel mode-${mode}`}>
			{/* WHICH KEYS AM I USING? The question this whole panel now answers
			    before anything else. The switch decides which environment's
			    credentials the token is minted from; the chip beside it, and the
			    one in the page's topbar, report what the token ACTUALLY is. */}
			<div className="mode-switch" role="group" aria-label="Which keys to use">
				<button
					type="button"
					className={`mode-opt sandbox${mode === 'sandbox' ? ' on' : ''}`}
					aria-pressed={mode === 'sandbox'}
					onClick={() => chooseMode('sandbox')}
				>
					<span className="mo-dot" aria-hidden="true" />
					<span className="mo-name">Sandbox keys</span>
					<span className="mo-sub">nothing billed</span>
				</button>
				<button
					type="button"
					className={`mode-opt production${mode === 'production' ? ' on' : ''}`}
					aria-pressed={mode === 'production'}
					onClick={() => chooseMode('production')}
				>
					<span className="mo-dot" aria-hidden="true" />
					<span className="mo-name">Production keys</span>
					<span className="mo-sub">real &amp; billed</span>
				</button>
			</div>
			<div className={`mode-now ${mode}`}>
				{mode === 'sandbox'
					? 'Showing SANDBOX keys — nothing is billed and no real applicant is touched.'
					: 'Showing PRODUCTION keys — every call runs a real check on a real person and is billed.'}
			</div>

			{/* THE ANSWER TO "WHICH KEYS IS IT USING", AT THE TOP OF THE PANEL.
			    The switch above says which environment you are LOOKING at; this
			    says which credential the console is SENDING with, and the two are
			    not the same thing — you can be reading the production card with a
			    sandbox token on the wire. It is deliberately empty-and-loud when
			    nothing has been picked, because "no keys" is the state most easily
			    mistaken for a working one. */}
			<div className={`inuse-now ${active ? (active.environment === 'live' ? 'live' : 'sandbox') : 'none'}`}>
				{active ? (
					<>
						<span className="inuse-tag">In use</span>
						<div className="inuse-body">
							<div className="inuse-line">
								<span className={`envchip ${active.environment}`}>
									{active.environment === 'live' ? 'PRODUCTION' : 'SANDBOX'}
								</span>
								<span className="inuse-label">{active.label}</span>
							</div>
							<div className="inuse-id mono">{active.clientId}</div>
						</div>
						<CopyButton value={active.clientId} what="the client ID in use" />
					</>
				) : (
					<>
						<span className="inuse-tag none">No keys</span>
						<div className="inuse-body">
							<div className="inuse-line">
								<span className="inuse-label">Nothing is authorised yet.</span>
							</div>
							<div className="inuse-hint">
								Press <strong>Use these keys</strong> on a key below — the console sends every request
								with it, and names it beside Send.
							</div>
						</div>
					</>
				)}
			</div>

			{/* ── SANDBOX ─────────────────────────────────────────────────── */}
			<section
				className={`keycard sandbox${mode === 'sandbox' ? ' active' : ' idle'}${
					active?.ref === 'sandbox' ? ' inuse' : ''
				}`}
			>
				<header className="keycard-head">
					<h4>Sandbox keys</h4>
					{/* No separate "in use" chip here — the button in this same header
					    already reads "In use" once pressed, and two labels saying the
					    same thing a centimetre apart read as two different states.
					    A production ROW does carry the chip, because its button is at
					    the far end of a long row. */}
					{/* In the header rather than down with the form, so it is reachable
					    while this card is the idle one too: choosing the sandbox key IS
					    how you come back from production. */}
					{/* NEVER DISABLED, NEVER DIMMED (Austin, 2026-09-24). This is the
					    one control on the card that answers "which keys is it using",
					    and a greyed-out button reads as "not available here" — which
					    was exactly wrong on the idle card, where switching back to
					    sandbox is the whole point. Re-entrancy is handled in the
					    handler rather than by taking the button away. */}
					<button
						type="button"
						className={`btn pri use-keys${active?.ref === 'sandbox' ? ' on' : ''}`}
						title="Mint a token from this key and send every request on this page with it"
						onClick={useSandboxKeys}
					>
						{busy ? 'Working…' : active?.ref === 'sandbox' ? 'In use' : 'Use these keys'}
					</button>
				</header>
				<p className="keycard-rule">
					Test keys. The secret is <strong>readable here whenever you need it</strong> — reveal it with the eye, copy it
					with the button. Sandbox requests never touch a real applicant and are never billed.
				</p>

				{mode === 'sandbox' ? (
					<>
				{loadErr ? (
					<div className="keyload-err">
						<span>{loadErr}</span>
						<button type="button" className="btn" onClick={() => loadKeys()}>
							Retry
						</button>
					</div>
				) : null}

				<div className="dfield">
					<div className="dflabel">Client ID</div>
					<div className="keyrow">
						<input
							className="mono"
							value={clientId}
							onChange={e => setClientId(e.target.value)}
							placeholder="32 characters"
							autoComplete="off"
							spellCheck={false}
						/>
						<CopyButton value={clientId} what="the client ID" />
					</div>
				</div>

				<div className="dfield">
					<div className="dflabel">Client Secret</div>
					<div className="keyrow">
						<input
							className="mono"
							type={showSecret ? 'text' : 'password'}
							value={clientSecret}
							onChange={e => setClientSecret(e.target.value)}
							placeholder="64 characters"
							autoComplete="off"
							spellCheck={false}
						/>
						<button
							type="button"
							className="keybtn"
							title={showSecret ? 'Hide the secret' : 'Show the secret'}
							aria-label={showSecret ? 'Hide the secret' : 'Show the secret'}
							aria-pressed={showSecret}
							onClick={() => setShowSecret(v => !v)}
						>
							<EyeIcon off={showSecret} />
						</button>
						<CopyButton value={clientSecret} what="the client secret" />
					</div>
					<div className="dfhint">
						{usingSandbox
							? 'Filled in with your own sandbox credential. '
							: 'Paste any credential here to mint a token with it. '}
						Held only in this tab&rsquo;s memory — never written to disk, and sent nowhere but{' '}
						<span className="mono">{trimSlash(baseUrl)}/oauth/token</span>. Gone on refresh.
					</div>
				</div>

				{sandbox?.expires_at ? (
					<div className="key-expiry">
						This key is rotated every {sandbox.ttl_hours ?? 24} hours — it expires{' '}
						{new Date(sandbox.expires_at).toLocaleString()}, and opening this page after
						that hands you the next one automatically.
					</div>
				) : null}

				{/* SCOPES AS A LIST, NOT AS SOMETHING YOU LOOK UP AND TYPE. A sandbox key
				    now carries every scope by default; this is here for narrowing one
				    deliberately, and for seeing at a glance what it holds. */}
				{sandbox?.id && allScopes.length ? (
					<div className="dfield scope-editor">
						<div className="dflabel">
							Scopes on this key{' '}
							<span className="fcount">
								{(editScopes ?? sandbox.scopes ?? []).length} of {allScopes.length}
							</span>
							<button
								type="button"
								className="btn scope-edit-toggle"
								onClick={() => {
									setScopeNote(null);
									setEditScopes(editScopes ? null : [...(sandbox.scopes ?? [])]);
								}}
							>
								{editScopes ? 'Cancel' : 'Edit'}
							</button>
						</div>
						{editScopes ? (
							<>
								<div className="scope-grid">
									{allScopes.map(sc => (
										<label key={sc} className={`scope-opt${editScopes.includes(sc) ? ' on' : ''}`}>
											<input
												type="checkbox"
												checked={editScopes.includes(sc)}
												onChange={e =>
													setEditScopes(cur =>
														e.target.checked
															? [...(cur ?? []), sc]
															: (cur ?? []).filter(x => x !== sc)
													)
												}
											/>
											<span className="mono">{sc}</span>
										</label>
									))}
								</div>
								<div className="auth-actions">
									<button type="button" className="btn" onClick={() => setEditScopes([...allScopes])}>
										All
									</button>
									<button
										type="button"
										className="btn"
										onClick={() => setEditScopes(allScopes.filter(x => x.endsWith(':read') || x === 'pii:full'))}
									>
										Reads only
									</button>
									<button type="button" className="btn" onClick={() => setEditScopes([])}>
										None
									</button>
									<button
										type="button"
										className="btn pri"
										disabled={savingScopes}
										onClick={async () => {
											const s = session();
											if (!s || !sandbox.id) return;
											setSavingScopes(true);
											setScopeNote(null);
											try {
												const res = await fetch(
													`${trimSlash(baseUrl)}/console/credentials/${sandbox.id}/scopes`,
													{
														method: 'PATCH',
														headers: { 'Content-Type': 'application/json', authorization: `Bearer ${s}` },
														body: JSON.stringify({ scopes: editScopes }),
													}
												);
												const body = await res.json().catch(() => null);
												const d = body?.data ?? body;
												if (!res.ok) throw new Error(d?.error?.message || `Could not save (${res.status}).`);
												setSandbox(cur => (cur ? { ...cur, scopes: d.scopes } : cur));
												setEditScopes(null);
												/* A token already minted still carries the old scopes, so it is
												   dropped rather than left to fail the next call. */
												onToken(null);
												setScopeNote(
													`Saved. ${d.added?.length ?? 0} added, ${d.removed?.length ?? 0} removed — generate a token for it to take effect.`
												);
												loadEvents();
											} catch (e: any) {
												setScopeNote(e?.message || 'Could not save the scopes.');
											} finally {
												setSavingScopes(false);
											}
										}}
									>
										{savingScopes ? 'Saving…' : 'Save scopes'}
									</button>
								</div>
							</>
						) : (
							<div className="scope-chips">
								{(sandbox.scopes ?? []).map(sc => (
									<span className="scope-chip" key={sc}>
										{sc}
									</span>
								))}
							</div>
						)}
						{scopeNote ? <div className="scope-note">{scopeNote}</div> : null}
					</div>
				) : null}

				<div className="dfield">
					<div className="dflabel">
						Narrow this token <span className="fcount">optional</span>
					</div>
					<input
						className="mono"
						value={scope}
						onChange={e => setScope(e.target.value)}
						placeholder="space-separated — default is every scope the credential holds"
					/>
				</div>
				<div className="auth-actions">
					<button
						type="button"
						className="btn pri"
						disabled={busy || !clientId || !clientSecret}
						title={
							!clientId || !clientSecret
								? 'Fill in a client ID and secret first — a token is minted from them.'
								: 'Mint a bearer token from these credentials'
						}
						onClick={() =>
							mint({
								ref: usingSandbox ? 'sandbox' : 'typed',
								clientId,
								clientSecret,
								scopeText: scope,
								label: usingSandbox ? 'Your sandbox key' : 'Pasted credential',
							})
						}
					>
						{busy ? 'Requesting…' : token ? 'Generate new token' : 'Generate token'}
					</button>
					<button
						type="button"
						className="btn"
						onClick={() => forget('sandbox')}
						disabled={!clientId && !clientSecret && !token}
					>
						Forget credentials
					</button>
					{sandbox && !usingSandbox ? (
						<button
							type="button"
							className="btn"
							onClick={() => {
								setClientId(sandbox.client_id);
								setClientSecret(sandbox.client_secret);
							}}
						>
							Use my sandbox key
						</button>
					) : null}
				</div>
					</>
				) : (
					<div className="card-idle">
						You are on production keys. Your sandbox id and secret stay readable here —
						press <strong>Use these keys</strong> above to send with them instead.
						{sandbox ? (
							<div className="keyrow idle-row">
								<span className="dflabel">Client ID</span>
								<span className="mono">{sandbox.client_id}</span>
								<CopyButton value={sandbox.client_id} what="the sandbox client ID" />
							</div>
						) : null}
					</div>
				)}
			</section>

			<div className="keysplit" aria-hidden="true">
				{'~'.repeat(120)}
			</div>

			{/* ── PRODUCTION ──────────────────────────────────────────────── */}
			<section
				className={`keycard production${mode === 'production' ? ' active' : ' idle'}${
					active && active.ref !== 'sandbox' && active.environment === 'live' ? ' inuse' : ''
				}`}
			>
				<header className="keycard-head">
					<h4>Production keys</h4>
				</header>
				<p className="keycard-rule">
					Live keys. The secret is shown <strong>once, when you create or rotate it</strong>, and is stored only as a
					hash — so it cannot be shown again here, and support cannot recover it. Production requests run real checks on
					real people and are billed.
				</p>

				{freshLive ? (
					<div className="fresh-secret">
						<div className="fresh-head">Copy this secret now — it will not be shown again.</div>
						<div className="dfield">
							<div className="dflabel">Client ID</div>
							<div className="keyrow">
								<input className="mono" readOnly value={freshLive.client_id} />
								<CopyButton value={freshLive.client_id} what="the production client ID" />
							</div>
						</div>
						<div className="dfield">
							<div className="dflabel">Client Secret</div>
							<div className="keyrow">
								<input className="mono" readOnly type={showFresh ? 'text' : 'password'} value={freshLive.client_secret} />
								<button
									type="button"
									className="keybtn"
									title={showFresh ? 'Hide the secret' : 'Show the secret'}
									aria-label={showFresh ? 'Hide the secret' : 'Show the secret'}
									aria-pressed={showFresh}
									onClick={() => setShowFresh(v => !v)}
								>
									<EyeIcon off={showFresh} />
								</button>
								<CopyButton value={freshLive.client_secret} what="the production client secret" />
							</div>
						</div>
						<button type="button" className="btn" onClick={() => setFreshLive(null)}>
							I&rsquo;ve stored it
						</button>
					</div>
				) : null}

				{live.length ? (
					<ul className="livekeys">
						{live.map(k => (
							<li key={k.id} className={active?.ref === k.id ? 'inuse' : undefined}>
								<div className="dfield">
									<div className="dflabel">
										{k.name || 'Production key'}
										{fmtDate(k.created_at) ? <span className="fcount"> created {fmtDate(k.created_at)}</span> : null}
										{active?.ref === k.id ? <span className="inuse-chip live">In use</span> : null}
									</div>
									<div className="keyrow">
										<input className="mono" readOnly value={k.client_id} />
										<CopyButton value={k.client_id} what="the production client ID" />
									</div>
								</div>
								<div className="dfield">
									<div className="dflabel">Client Secret</div>
									<div className="keyrow secret-gone">
										<span className="mono">{'•'.repeat(32)}</span>
										<span className="gone-note">shown once at creation — not recoverable</span>
									</div>
								</div>
								{/* SCOPES, on a production key too — the same control as the
								    sandbox one, because "what can this key do" is the same
								    question whichever environment it is in. */}
								{allScopes.length ? (
									<div className="dfield scope-editor">
										<div className="dflabel">
											Scopes{' '}
											<span className="fcount">
												{(liveEdit?.id === k.id ? liveEdit.scopes : k.scopes ?? []).length} of{' '}
												{allScopes.length}
											</span>
											<button
												type="button"
												className="btn scope-edit-toggle"
												onClick={() =>
													setLiveEdit(
														liveEdit?.id === k.id ? null : { id: k.id, scopes: [...(k.scopes ?? [])] }
													)
												}
											>
												{liveEdit?.id === k.id ? 'Cancel' : 'Edit'}
											</button>
										</div>
										{liveEdit?.id === k.id ? (
											<>
												<div className="scope-grid">
													{allScopes.map(sc => (
														<label key={sc} className={`scope-opt${liveEdit.scopes.includes(sc) ? ' on' : ''}`}>
															<input
																type="checkbox"
																checked={liveEdit.scopes.includes(sc)}
																onChange={e =>
																	setLiveEdit(cur =>
																		cur
																			? {
																					id: cur.id,
																					scopes: e.target.checked
																						? [...cur.scopes, sc]
																						: cur.scopes.filter(x => x !== sc),
																				}
																			: cur
																	)
																}
															/>
															<span className="mono">{sc}</span>
														</label>
													))}
												</div>
												<div className="auth-actions">
													<button
														type="button"
														className="btn"
														onClick={() => setLiveEdit({ id: k.id, scopes: [...allScopes] })}
													>
														All
													</button>
													<button
														type="button"
														className="btn"
														onClick={() =>
															setLiveEdit({
																id: k.id,
																scopes: allScopes.filter(x => x.endsWith(':read') || x === 'pii:full'),
															})
														}
													>
														Reads only
													</button>
													<button
														type="button"
														className="btn pri"
														disabled={liveBusy === k.id}
														onClick={async () => {
															const s = session();
															if (!s) return;
															setLiveBusy(k.id);
															try {
																const res = await fetch(
																	`${trimSlash(baseUrl)}/console/credentials/${k.id}/scopes`,
																	{
																		method: 'PATCH',
																		headers: {
																			'Content-Type': 'application/json',
																			authorization: `Bearer ${s}`,
																		},
																		body: JSON.stringify({ scopes: liveEdit.scopes }),
																	}
																);
																const body = await res.json().catch(() => null);
																const d = body?.data ?? body;
																if (!res.ok) throw new Error(d?.error?.message || `Could not save (${res.status}).`);
																setLive(cur =>
																	cur.map(x => (x.id === k.id ? { ...x, scopes: d.scopes } : x))
																);
																setLiveEdit(null);
																setLiveNote({
																	id: k.id,
																	text: `Saved. ${d.added?.length ?? 0} added, ${d.removed?.length ?? 0} removed — generate a token for it to take effect.`,
																});
																loadEvents();
															} catch (e: any) {
																setLiveNote({ id: k.id, text: e?.message || 'Could not save.', bad: true });
															} finally {
																setLiveBusy(null);
															}
														}}
													>
														Save scopes
													</button>
												</div>
											</>
										) : (
											<div className="scope-chips">
												{(k.scopes ?? []).length ? (
													(k.scopes ?? []).map(sc => (
														<span className="scope-chip" key={sc}>
															{sc}
														</span>
													))
												) : (
													<span className="chint">none</span>
												)}
											</div>
										)}
									</div>
								) : null}

								<div className="auth-actions">
									{/* The same words as on the sandbox card, doing the same thing —
									    a production key's secret is a hash, so this mints through the
									    panel session rather than through /oauth/token. The developer
									    should not have to know that; the button is the same button. */}
									<button
										type="button"
										className={`btn pri use-keys${active?.ref === k.id ? ' on' : ''}`}
										title="Mint a token from this key and send every request on this page with it"
										onClick={() => mintFor(k.id, k.name || 'Production key', k.client_id)}
									>
										{liveBusy === k.id ? 'Working…' : active?.ref === k.id ? 'In use' : 'Use these keys'}
									</button>
									{mode === 'production' ? (
										<button
											type="button"
											className="btn"
											title="Put this client ID in the form below, to mint with a secret you stored"
											onClick={() => {
												setLiveId(k.client_id);
												setLiveSecret('');
												setShowLiveSecret(false);
											}}
										>
											Use this ID below
										</button>
									) : null}
									<button
										type="button"
										className="btn danger"
										disabled={liveBusy === k.id}
										onClick={() => deleteLive(k.id, k.client_id)}
									>
										Delete
									</button>
								</div>
								{liveBusy === k.id && passkeyStep ? (
									<div className="live-note passkey-step">{passkeyStep}</div>
								) : null}
								{liveNote?.id === k.id ? (
									<div className={`live-note${liveNote.bad ? ' bad' : ''}`}>{liveNote.text}</div>
								) : null}
							</li>
						))}
					</ul>
				) : (
					<div className="ac-hint">
						{loaded ? 'No production keys yet.' : 'Sign in to the panel to see your keys.'}
					</div>
				)}

				{cardNote ? <div className="live-note card-note">{cardNote}</div> : null}

				<div className="auth-actions">
					<button type="button" className="btn" disabled={creating} onClick={createLive}>
						{creating ? 'Creating…' : 'Create a production key'}
					</button>
				</div>

				{mode === 'production' ? (
					<div className="live-form">
						<div className="live-form-head">Mint a production token</div>
						<div className="dfield">
							<div className="dflabel">Client ID</div>
							<div className="keyrow">
								<input
									className="mono"
									value={liveId}
									onChange={e => setLiveId(e.target.value)}
									placeholder="paste, or use one of the keys above"
									autoComplete="off"
									spellCheck={false}
								/>
								<CopyButton value={liveId} what="the client ID" />
							</div>
						</div>
						<div className="dfield">
							<div className="dflabel">Client Secret</div>
							<div className="keyrow">
								<input
									className="mono"
									type={showLiveSecret ? 'text' : 'password'}
									value={liveSecret}
									onChange={e => setLiveSecret(e.target.value)}
									placeholder="the secret you stored when the key was created"
									autoComplete="off"
									spellCheck={false}
								/>
								<button
									type="button"
									className="keybtn"
									title={showLiveSecret ? 'Hide the secret' : 'Show the secret'}
									aria-label={showLiveSecret ? 'Hide the secret' : 'Show the secret'}
									aria-pressed={showLiveSecret}
									onClick={() => setShowLiveSecret(v => !v)}
								>
									<EyeIcon off={showLiveSecret} />
								</button>
							</div>
							<div className="dfhint">
								A production secret cannot be fetched — it was shown once when the key was
								created. Paste the copy you stored. Held only in this tab&rsquo;s memory and sent
								nowhere but <span className="mono">{trimSlash(baseUrl)}/oauth/token</span>.
							</div>
						</div>
						<div className="dfield">
							<div className="dflabel">
								Scope <span className="fcount">optional</span>
							</div>
							<input
								className="mono"
								value={liveScope}
								onChange={e => setLiveScope(e.target.value)}
								placeholder="space-separated — default is every scope the credential holds"
							/>
						</div>
						<div className="auth-actions">
							<button
								type="button"
								className="btn pri"
								disabled={busy || !liveId || !liveSecret}
								title={
									!liveId || !liveSecret
										? 'Paste the production client ID and the secret you stored when the key was created.'
										: 'Mint a production token from these credentials'
								}
								onClick={() =>
									mint({
										ref: 'typed',
										clientId: liveId,
										clientSecret: liveSecret,
										scopeText: liveScope,
										label: 'Pasted production credential',
									})
								}
							>
								{busy ? 'Requesting…' : token ? 'Generate new token' : 'Generate token'}
							</button>
							<button
								type="button"
								className="btn"
								onClick={() => forget('production')}
								disabled={!liveId && !liveSecret && !token}
							>
								Forget credentials
							</button>
						</div>
					</div>
				) : null}
			</section>

			{err ? <div className="dferr">{err}</div> : null}
			{token ? (
				<div className={`token-status${expired ? ' expired' : ''}`}>
					<div className="token-row">
						<span className={`vch ${expired ? 'rej' : 'ok'}`}>{expired ? 'expired' : 'token active'}</span>
						<span className={`envchip ${token.environment === 'live' ? 'live' : token.environment === 'sandbox' ? 'sandbox' : 'unknown'}`}>
							{token.environment === 'live'
								? 'PRODUCTION'
								: token.environment === 'sandbox'
									? 'SANDBOX'
									: 'environment unknown'}
						</span>
						<span className="token-exp mono">
							{expired ? 'mint a new one to keep going' : `expires in ${fmtRemaining(remainingMs)}`}
						</span>
					</div>
					{/* A PASTED CREDENTIAL CAN DISAGREE WITH THE TAB IT WAS PASTED INTO,
					    and that is the dangerous case. It is limited to pasted ones on
					    purpose: a key chosen with "Use these keys" sets the tab itself,
					    so the two can only differ once you switch tabs afterwards —
					    which is looking, not a mistake, and warning about it every time
					    would teach people to read past the warning that matters. */}
					{token.environment &&
					active?.ref === 'typed' &&
					(token.environment === 'live') !== (mode === 'production') ? (
						<div className="env-mismatch">
							This token is a {token.environment === 'live' ? 'PRODUCTION' : 'SANDBOX'} token,
							but you have {mode === 'production' ? 'Production' : 'Sandbox'} selected. The
							credential decides, not the switch.
						</div>
					) : null}
					<div className="scope-chips">
						{token.scope
							.split(' ')
							.filter(Boolean)
							.map(s => (
								<span className="scope-chip" key={s}>
									{s}
								</span>
							))}
					</div>
				</div>
			) : null}

			{/* ── HISTORY ─────────────────────────────────────────────────────
			    WHO DID WHAT, AND WHEN (Austin, 2026-09-24).

			    Last in the drawer because it is the reference, not the control:
			    everything above it is something to press, and this is the record
			    of what pressing it did. It covers keys that no longer exist —
			    the deletion of a production key is the entry most worth having,
			    and it is precisely the one a per-key view could never show. */}
			<section className="keyhistory">
				<header className="keyhistory-head">
					<h4>History</h4>
					{events?.length ? <span className="fcount">{events.length}</span> : null}
					{/* No Refresh. It says it is live and it is: this marker only shows
					    once a first load has succeeded, so it never promises freshness
					    over a list that failed to load. */}
					{events && !eventsErr ? (
						<span className={`kh-live${eventsBusy ? ' on' : ''}`} title="Updates on its own">
							<span className="kh-live-dot" aria-hidden="true" />
							Live
						</span>
					) : null}
				</header>
				<p className="keyhistory-rule">
					Every key created, re-scoped, rotated or deleted on this account, and each
					production token minted here. Kept after the key itself is gone.
				</p>

				{eventsErr ? <div className="keyload-err"><span>{eventsErr}</span></div> : null}

				{events && !events.length && !eventsErr ? (
					<div className="ac-hint">Nothing recorded yet \u2014 the first change to a key appears here.</div>
				) : null}

				{events?.length ? (
					<>
						<ol className="keyhistory-list">
							{(allEvents ? events : events.slice(0, 8)).map(e => (
								<li key={e.id} className={e.environment === 'live' ? 'live' : 'sandbox'}>
									<div className="kh-top">
										<span className={`vch ${ACTION_TONE[e.action] ?? 'ns'}`}>
											{e.environment === 'live' ? 'PROD' : 'SANDBOX'}
										</span>
										<span className="kh-what">{actionLabel(e)}</span>
									</div>
									<div className="kh-who">
										{actorLabel(e)}
										{e.actor?.role ? <span className="kh-role">{e.actor.role}</span> : null}
									</div>
									{/* The stamp and the key it names, on their own line: this is
									    what the log is read for, so it is never abbreviated away. */}
									<div className="kh-when mono">
										{fmtStamp(e.at)}
										{fmtAgo(e.at) ? <span className="kh-ago">{fmtAgo(e.at)}</span> : null}
										<span className="kh-key" title={e.client_id}>
											{e.credential_name ? `${e.credential_name} \u00b7 ` : ''}
											{shortId(e.client_id)}
										</span>
										{e.ip ? <span className="kh-ip">{e.ip.replace(/^::ffff:/, '')}</span> : null}
									</div>
								</li>
							))}
						</ol>
						{events.length > 8 ? (
							<button type="button" className="btn" onClick={() => setAllEvents(v => !v)}>
								{allEvents ? 'Show the latest 8' : `Show all ${events.length}`}
							</button>
						) : null}
					</>
				) : null}
			</section>
		</div>
	);
};
